import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
// material-ui
import { styled, useTheme } from '@mui/material/styles';
import { Avatar, Box, Grid, Typography, Stack, Tooltip, IconButton, LinearProgress, Chip, Fade } from '@mui/material';

// project imports
import MainCard from 'ui-component/cards/MainCard';
import TotalIncomeCard from 'ui-component/cards/Skeleton/TotalIncomeCard';

// assets
import RestaurantIcon from '@mui/icons-material/Restaurant';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import LocalDiningIcon from '@mui/icons-material/LocalDining';
import KitchenIcon from '@mui/icons-material/Kitchen';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import InventoryIcon from '@mui/icons-material/Inventory';

// ==============================|| STYLED COMPONENTS ||============================== //

const ProfessionalCardWrapper = styled(MainCard)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.primary,
  overflow: 'hidden',
  position: 'relative',
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: '12px',
  boxShadow: '0 2px 10px 0 rgba(0, 0, 0, 0.03)',
  transition: 'all 0.3s ease',
  minHeight: '160px',
  maxHeight: '160px',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 24px 0 rgba(0, 0, 0, 0.08)',
    borderColor: '#e65100' // Dark orange accent
  }
}));

const CategoryChip = styled(Chip)(({ theme }) => ({
  height: 24,
  fontSize: '0.75rem',
  fontWeight: 600,
  backgroundColor: 'rgba(245, 124, 0, 0.1)',
  color: '#e65100',
  border: 'none',
  '& .MuiChip-icon': {
    fontSize: '0.875rem',
    color: 'inherit'
  }
}));

const InfoIconButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: 8,
  right: 8,
  color: theme.palette.text.secondary,
  padding: 4,
  '&:hover': {
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.action.hover
  }
}));

// ==============================|| UTILITIES ||============================== //

const calculateFoodStats = (foodItemData) => {
  if (!foodItemData || foodItemData.length === 0) {
    return {
      total: 0,
      categories: {},
      avgPrice: 0,
      inStock: 0,
      outOfStock: 0,
      popularItems: 0
    };
  }

  const stats = {
    total: foodItemData.length,
    categories: {},
    avgPrice: 0,
    inStock: 0,
    outOfStock: 0,
    popularItems: 0
  };

  let totalPrice = 0;

  foodItemData.forEach((item) => {
    // Contar por categoría
    const category = item.category || 'Sin categoría';
    stats.categories[category] = (stats.categories[category] || 0) + 1;

    // Calcular precio promedio
    totalPrice += parseFloat(item.price || 0);

    // Contar stock
    if (item.inStock !== false && item.quantity > 0) {
      stats.inStock++;
    } else {
      stats.outOfStock++;
    }

    // Contar items populares (basado en algún criterio como ventas o rating)
    if (item.isPopular || item.rating >= 4.5 || item.soldCount > 50) {
      stats.popularItems++;
    }
  });

  stats.avgPrice = stats.total > 0 ? (totalPrice / stats.total).toFixed(2) : 0;

  return stats;
};

// ==============================|| COMPONENTE PRINCIPAL ||============================== //

const TotalFoodItems = ({ isLoading, foodItemData }) => {
  const theme = useTheme();
  const [stats, setStats] = useState({
    total: 0,
    categories: {},
    avgPrice: 0,
    inStock: 0,
    outOfStock: 0,
    popularItems: 0
  });
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    if (foodItemData) {
      setStats(calculateFoodStats(foodItemData));
    }
  }, [foodItemData]);

  const stockPercentage = stats.total > 0 ? ((stats.inStock / stats.total) * 100).toFixed(1) : 0;

  const categoriesCount = Object.keys(stats.categories).length;

  if (isLoading) {
    return <TotalIncomeCard />;
  }

  return (
    <Fade in={true} timeout={500}>
      <ProfessionalCardWrapper border={false} content={false}>
        <Box sx={{ p: 2.5, position: 'relative', height: '100%' }}>
          {/* Botón de información */}
          <Tooltip title="Detalles del menú">
            <InfoIconButton size="small" onClick={() => setShowDetails(!showDetails)}>
              <InfoOutlinedIcon sx={{ fontSize: 16 }} />
            </InfoIconButton>
          </Tooltip>

          <Grid container spacing={1} height="100%">
            {/* Sección Principal */}
            <Grid item xs={12}>
              <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
                <Box flex={1}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: theme.palette.text.secondary,
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      letterSpacing: '0.5px',
                      textTransform: 'uppercase',
                      mb: 0.5
                    }}
                  >
                    Resumen Menú
                  </Typography>

                  <Stack direction="row" alignItems="baseline" spacing={1}>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 700,
                        fontSize: '2rem',
                        lineHeight: 1,
                        color: theme.palette.text.primary,
                        letterSpacing: '-0.5px'
                      }}
                    >
                      {stats.total}
                    </Typography>

                    <CategoryChip icon={<LocalDiningIcon />} label={`${categoriesCount} cats`} size="small" />
                  </Stack>

                  {/* Precio promedio */}
                  <Typography
                    variant="body2"
                    sx={{
                      color: theme.palette.text.secondary,
                      mt: 1,
                      fontSize: '0.75rem',
                      fontWeight: 500
                    }}
                  >
                    Precio promedio: <span style={{ color: theme.palette.text.primary, fontWeight: 600 }}>${stats.avgPrice}</span>
                  </Typography>
                </Box>

                {/* Avatar/Icono */}
                <Avatar
                  sx={{
                    width: 48,
                    height: 48,
                    backgroundColor: 'rgba(230, 81, 0, 0.08)',
                    color: '#e65100',
                    borderRadius: '12px'
                  }}
                >
                  <RestaurantMenuIcon sx={{ fontSize: 24 }} />
                </Avatar>
              </Stack>
            </Grid>

            {/* Sección de Estadísticas */}
            <Grid item xs={12}>
              <Box sx={{ mt: 1.5 }}>
                {/* Barra de stock */}
                <Stack spacing={0.5}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography
                      variant="body2"
                      sx={{
                        color: theme.palette.text.secondary,
                        fontSize: '0.75rem',
                        fontWeight: 500
                      }}
                    >
                      Disponibilidad
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: theme.palette.text.primary,
                        fontWeight: 700,
                        fontSize: '0.75rem'
                      }}
                    >
                      {stats.inStock} en stock <span style={{ color: theme.palette.text.secondary, fontWeight: 500 }}>({stockPercentage}%)</span>
                    </Typography>
                  </Stack>

                  <LinearProgress
                    variant="determinate"
                    value={parseFloat(stockPercentage)}
                    sx={{
                      height: 6,
                      borderRadius: 3,
                      backgroundColor: theme.palette.grey[200],
                      '& .MuiLinearProgress-bar': {
                        borderRadius: 3,
                        backgroundColor: '#e65100'
                      }
                    }}
                  />
                </Stack>

                {/* Información adicional expandible */}
                {showDetails && (
                  <Fade in={showDetails}>
                    <Box sx={{ 
                        mt: 2, 
                        p: 1.5,
                        backgroundColor: theme.palette.action.hover, 
                        borderRadius: '8px',
                        border: `1px solid ${theme.palette.divider}`
                    }}>
                      <Stack spacing={1}>
                        <Stack direction="row" justifyContent="space-between">
                          <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 500 }}>
                            Artículos populares
                          </Typography>
                          <Typography variant="caption" sx={{ color: theme.palette.text.primary, fontWeight: 600 }}>
                            {stats.popularItems}
                          </Typography>
                        </Stack>
                        <Stack direction="row" justifyContent="space-between">
                          <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 500 }}>
                            Sin stock
                          </Typography>
                          <Typography variant="caption" sx={{ color: theme.palette.error.main, fontWeight: 600 }}>
                            {stats.outOfStock}
                          </Typography>
                        </Stack>
                        {Object.entries(stats.categories)
                          .slice(0, 2)
                          .map(([category, count]) => (
                            <Stack key={category} direction="row" justifyContent="space-between">
                              <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                                {category}
                              </Typography>
                              <Typography variant="caption" sx={{ color: theme.palette.text.primary, fontWeight: 600 }}>
                                {count}
                              </Typography>
                            </Stack>
                          ))}
                      </Stack>
                    </Box>
                  </Fade>
                )}
              </Box>
            </Grid>
          </Grid>
        </Box>
      </ProfessionalCardWrapper>
    </Fade>
  );
};

TotalFoodItems.propTypes = {
  isLoading: PropTypes.bool,
  foodItemData: PropTypes.array
};

export default TotalFoodItems;
